/* Attach only the explicitly selected product to an enquiry; no browsing history is stored. */
(() => {
  if (window.zenvanaProductQuestionReady) return;
  window.zenvanaProductQuestionReady = true;
  const clean = (value, limit = 240) => String(value || '').replace(/[\u0000-\u001f]/g, ' ').trim().slice(0, limit);
  const optionText = select => clean(select?.selectedOptions?.[0]?.textContent);

  function prepareLink(link) {
    const product = link.closest('product-info') || document.querySelector('product-info[data-product-id]');
    const handle = product?.querySelector('[data-product-handle]')?.dataset.productHandle;
    if (!handle) return;
    const url = new URL(link.href, location.origin);
    url.searchParams.set('product', handle);
    ['material', 'size', 'quantity', 'variant'].forEach(key => url.searchParams.delete(key));
    const mural = product.querySelector('[data-mural-customizer]');
    let material;
    let size;
    if (mural) {
      material = clean(mural.querySelector('.mural-cta__material.is-selected .mural-cta__material-name')?.textContent);
      size = [...mural.querySelectorAll('[data-pcta-dim]')]
        .filter(input => input.getClientRects().length && input.value !== '')
        .map(input => `${clean(input.getAttribute('aria-label') || input.dataset.pctaDim)}: ${clean(input.value, 20)}`)
        .join('; ');
    } else {
      material = optionText(product.querySelector('[data-wc-material]'));
      size = optionText(product.querySelector('[data-wc-size]'));
      const variant = product.querySelector('input.product-variant-id')?.value;
      if (/^\d+$/.test(variant || '')) url.searchParams.set('variant', variant);
    }
    if (material) url.searchParams.set('material', material);
    if (size) url.searchParams.set('size', clean(size, 500));
    const quantity = product.querySelector('.product__info-container input[name="quantity"]')?.value;
    if (quantity && /^\d{1,5}$/.test(quantity)) url.searchParams.set('quantity', quantity);
    link.href = url.href;
  }
  // Refresh just before activation, including keyboard and open-in-new-tab gestures.
  ['pointerover', 'pointerdown', 'focusin', 'click', 'contextmenu'].forEach(type => {
    document.addEventListener(type, event => {
      const link = event.target.closest?.('a[href]');
      const reference = document.querySelector('[data-product-question] a');
      if (!link || !reference) return;
      const target = new URL(link.href, location.origin);
      const contact = new URL(reference.href, location.origin);
      if (target.origin === contact.origin && target.pathname === contact.pathname) prepareLink(link);
    });
  });

  const mount = document.querySelector('[data-enquiry-form]');
  if (!mount) return;
  const context = mount.querySelector('[data-enquiry-context]');
  const input = mount.querySelector('[data-enquiry-url]');
  const status = mount.querySelector('[data-enquiry-status]');
  const selection = mount.querySelector('[data-enquiry-selection]');
  const store = new URL(mount.dataset.storeUrl);
  const root = mount.dataset.rootUrl || '/';
  const params = new URLSearchParams(location.search);
  let request = 0;
  let controller;
  let loading = false;
  const form = mount.closest('form');
  const submit = form.querySelector('[type="submit"]');
  const support = document.querySelector('[data-support-form]');
  let supportForm;
  let attachedProduct = null;
  const referenceNames = ['hidden', 'hidden-1', 'hidden-2', 'hidden-3', 'hidden-4', 'hidden-5'];
  let nativeEdited = false;
  form.addEventListener('input', event => { if (!mount.contains(event.target)) nativeEdited = true; });

  function syncSupport() {
    if (!supportForm?.isConnected) return;
    const attached = !context.hidden && attachedProduct;
    const logo = support.dataset.supportLogo ? new URL(support.dataset.supportLogo, store.origin).href : '';
    const values = {
      hidden: attached ? attachedProduct.title : 'General enquiry',
      'hidden-1': attached ? attachedProduct.sku : 'Not applicable',
      'hidden-2': attached ? String(attachedProduct.id) : 'Not applicable',
      'hidden-3': attached ? attachedProduct.image || logo : logo,
      'hidden-4': attached ? attachedProduct.url : store.origin,
      'hidden-5': selection.value.replace(/\r?\n/g, ' · ') || 'Not specified'
    };
    Object.entries(values).forEach(([name, value]) => {
      const field = supportForm.elements.namedItem(name);
      if (!field) return;
      // App hydration or form resets must not restore a previous enquiry.
      field.defaultValue = value;
      if (field.value === value) return;
      field.value = value;
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    });
    const button = supportForm.querySelector('button.submit');
    if (button && loading) {
      button.disabled = true;
      button.dataset.enquiryLoading = 'true';
    } else if (button?.dataset.enquiryLoading) {
      button.disabled = false;
      delete button.dataset.enquiryLoading;
    }
  }

  function connectSupport() {
    if (!support || nativeEdited || form.querySelector('.form-status')) return false;
    const candidate = support.querySelector('form[data-id="166840"]');
    const names = referenceNames;
    if (!candidate?.querySelector('button.submit') || names.some(name => !candidate.elements.namedItem(name))) return false;
    supportForm = candidate;
    let details = candidate.querySelector('.z-support-references');
    if (!details) {
      details = document.createElement('details');
      details.className = 'z-support-references';
      const summary = document.createElement('summary');
      summary.textContent = 'Product references included with your message';
      details.append(summary);
      candidate.querySelector('.panel').append(details);
    }
    names.forEach(name => {
      const field = candidate.elements.namedItem(name);
      field.readOnly = true;
      const wrapper = field.closest('.globo-form-control');
      if (wrapper && !details.contains(wrapper)) details.append(wrapper);
    });
    // Keep the existing product picker outside either form. Its values are copied
    // into ordinary app fields, which remain readable in the references section.
    if (mount.nextElementSibling !== support) support.before(mount);
    form.hidden = true;
    support.classList.add('is-ready');
    candidate.querySelector('.gfb__page-header')?.setAttribute('hidden', '');
    const button = candidate.querySelector('button.submit');
    if (!button.dataset.enquiryLabel) {
      button.textContent = 'Send message';
      button.dataset.enquiryLabel = 'true';
    }
    const email = candidate.elements.namedItem('email');
    if (email) { email.type = 'email'; email.inputMode = 'email'; }
    if (!candidate.querySelector('.z-support-privacy')) {
      const note = document.createElement('p');
      note.className = 'z-support-privacy';
      note.textContent = 'We use your details to respond to your request. Sending this form does not subscribe you to marketing emails.';
      candidate.append(note);
    }
    syncSupport();
    return true;
  }
  if (support) {
    // The app may replace the form after hydration; keep the current form bound.
    const observer = new MutationObserver(() => { connectSupport(); });
    observer.observe(support, { childList: true, subtree: true });
    connectSupport();
    // Capture before app click/submit handlers serialize their model. No submit
    // is triggered here, and upload/CAPTCHA fields are never read or changed.
    const beforeSend = event => {
      if (event.type === 'click' && !event.target.closest?.('button.submit')) return;
      if (!event.target.closest?.('form[data-id="166840"]')) return;
      connectSupport();
      if (loading) {
        event.preventDefault(); event.stopImmediatePropagation();
        status.textContent = 'Please wait a moment while the product is attached.';
      } else syncSupport();
    };
    support.addEventListener('click', beforeSend, true);
    support.addEventListener('submit', beforeSend, true);
    support.addEventListener('reset', () => setTimeout(connectSupport, 0), true);
    window.addEventListener('pageshow', connectSupport);
  }

  function clearContext() {
    attachedProduct = null;
    context.hidden = true;
    context.disabled = true;
    selection.value = '';
    context.querySelectorAll('input').forEach(field => { field.value = ''; });
  }
  function finishLoading() {
    loading = false;
    submit.disabled = false;
    syncSupport();
  }
  function productHandle(value) {
    try {
      const url = new URL(value, store.origin);
      if (!['https:', 'http:'].includes(url.protocol) || ![store.host, location.host].includes(url.host)) return null;
      return url.pathname.match(/\/products\/([a-zA-Z0-9-]+)\/?$/)?.[1] || null;
    } catch { return null; }
  }
  async function loadProduct(handle, details = '', variantId = '') {
    const current = ++request;
    controller?.abort();
    controller = new AbortController();
    const activeController = controller;
    clearContext();
    loading = true;
    submit.disabled = true;
    syncSupport();
    status.textContent = 'Adding the product to your message…';
    const timer = setTimeout(() => activeController.abort(), 10000);
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { signal: activeController.signal });
      if (!response.ok) throw new Error('Product unavailable');
      const product = await response.json();
      if (current !== request) return;
      if (product.handle !== handle || !product.title || !product.id) throw new Error('Product unavailable');
      const productURL = new URL(`${root}products/${handle}`, store.origin).href;
      const title = context.querySelector('[data-enquiry-title]');
      title.textContent = product.title;
      title.href = productURL;
      const image = context.querySelector('[data-enquiry-image]');
      image.hidden = true;
      image.removeAttribute('src');
      if (product.featured_image) {
        const imageURL = new URL(product.featured_image, store.origin);
        if (imageURL.protocol === 'https:' && [store.host, 'cdn.shopify.com', location.host].includes(imageURL.host)) {
          image.src = imageURL.href;
          image.alt = product.title;
          image.hidden = false;
          context.querySelector('[data-enquiry-image-url]').value = imageURL.href;
        }
      }
      context.querySelector('[data-enquiry-name]').value = product.title;
      context.querySelector('[data-enquiry-id]').value = product.id;
      const variants = product.variants || [];
      const variant = variants.find(item => String(item.id) === variantId);
      const skus = variants.map(item => clean(item.sku));
      // A shared product code is safe without a variant; never guess between different SKUs.
      const sharedSKU = skus.length && skus.every(sku => sku && sku === skus[0]) ? skus[0] : '';
      context.querySelector('[data-enquiry-sku]').value = clean(variant?.sku) || sharedSKU || 'Not specified';
      attachedProduct = { title: product.title, id: product.id, url: productURL,
        sku: context.querySelector('[data-enquiry-sku]').value,
        image: context.querySelector('[data-enquiry-image-url]').value };
      input.value = productURL;
      selection.value = details;
      context.hidden = false;
      context.disabled = false;
      status.textContent = 'Product attached. Add your question below.';
    } catch {
      if (current === request) status.textContent = 'The product preview could not load. Your product link will still be included with your message.';
    } finally {
      clearTimeout(timer);
      if (current === request) finishLoading();
    }
  }
  // Clear stale product information as soon as a different link is entered.
  let linkTimer;
  function attachEnteredProduct() {
    clearTimeout(linkTimer);
    const value = input.value.trim();
    const handle = productHandle(value);
    if (handle) loadProduct(handle, '', new URL(value, store.origin).searchParams.get('variant') || '');
    else status.textContent = value ? 'Use a ZenvanaHome product link to attach a design, or include other links in your message.' : 'No product attached. You can send a general question.';
  }
  input.addEventListener('input', () => {
    clearTimeout(linkTimer);
    ++request;
    controller?.abort();
    clearContext();
    finishLoading();
    status.textContent = 'Add a product link, or leave this blank for a general question.';
    if (productHandle(input.value.trim())) {
      loading = true;
      submit.disabled = true;
      syncSupport();
      status.textContent = 'Adding the product to your message…';
      linkTimer = setTimeout(attachEnteredProduct, 400);
    }
  });
  input.addEventListener('change', attachEnteredProduct);
  selection.addEventListener('input', () => {
    context.querySelector('[data-enquiry-sku]').value = 'Selection edited — confirm SKU';
    if (attachedProduct) attachedProduct.sku = 'Selection edited — confirm SKU';
    syncSupport();
  });
  mount.querySelector('[data-enquiry-remove]').addEventListener('click', () => {
    clearTimeout(linkTimer);
    ++request;
    controller?.abort();
    clearContext();
    finishLoading();
    input.value = '';
    syncSupport();
    status.textContent = 'Product removed. You can send a general question.';
    input.focus();
  });
  form.addEventListener('submit', event => {
    if (loading) {
      event.preventDefault();
      status.textContent = 'Please wait a moment while the product is attached.';
    }
  });
  const handle = params.get('product');
  if (handle && /^[a-zA-Z0-9-]{1,200}$/.test(handle)) {
    input.value = new URL(`${root}products/${handle}`, store.origin).href;
    const details = [['material', 'Material'], ['size', 'Size'], ['quantity', 'Quantity']]
      .map(([key, label]) => params.get(key) ? `${label}: ${clean(params.get(key), 500)}` : '')
      .filter(Boolean).join('\n');
    loadProduct(handle, details, params.get('variant') || '');
  }
})();
